import type { FC } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import type { PageOfShipmentDto } from '@/api';
import { Badge, Card, CardHeader, CardTitle } from '@/ui';

import { formatDate, toNumber } from '../../lib/format';
import { SHIPMENT_STATUSES } from '../../lib/shipmentStatus';

const formatDelayCount = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return `${count} задержка`;
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
    return `${count} задержки`;
  return `${count} задержек`;
};

/*
 * «Задержанные отгрузки» - блок макета под задачами: клиент и номер, причина,
 * плановая дата, статус. Задержанных нет - блока нет. Показаны первые; если их
 * больше - ссылка на список «Отгрузки в работе» с фильтром «Только задержанные».
 */
export const DelayedShipments: FC<{ page: PageOfShipmentDto | undefined }> = ({
  page,
}) => {
  const shipments = page?.items ?? [];
  const total = toNumber(page?.totalRecords ?? 0);

  if (total === 0) return null;

  return (
    <Card className="mb-[22px]">
      <CardHeader>
        <div className="flex items-center gap-2.5">
          <Badge tone="delayed" className="h-[26px] px-2.5 text-xs">
            <AlertTriangle aria-hidden className="size-3" />
            {formatDelayCount(total)}
          </Badge>
          <CardTitle>Задержанные отгрузки</CardTitle>
        </div>
        {total > shipments.length && (
          <Link
            to="/shipments"
            search={{ delayed: true }}
            className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-[550] whitespace-nowrap text-ink-700 hover:bg-ink-100"
          >
            Все задержанные <ArrowRight aria-hidden className="size-3" />
          </Link>
        )}
      </CardHeader>

      {shipments.map((shipment) => {
        const status = SHIPMENT_STATUSES[shipment.status];

        return (
          <div
            key={shipment.id}
            className="relative flex items-center gap-3.5 border-b border-ink-150 px-4 py-3 last:border-b-0 hover:bg-ink-50 has-[a:focus-visible]:bg-ink-50"
          >
            <span className="grid size-7 shrink-0 place-items-center rounded-[7px] bg-st-delayed-bg text-st-delayed">
              <AlertTriangle aria-hidden className="size-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <Link
                to="/shipments/$shipmentId"
                params={{ shipmentId: shipment.id }}
                className="text-[13px] font-[550] text-ink-900 outline-none after:absolute after:inset-0 after:content-['']"
              >
                {shipment.partnerName} - № {shipment.number}
              </Link>
              <div className="mt-0.5 truncate text-xs text-ink-500">
                Причина: {shipment.delayReason}
              </div>
            </div>
            <span className="w-[130px] shrink-0 text-xs text-ink-500">
              План: {formatDate(shipment.plannedDate)}
            </span>
            <span className="w-[130px] shrink-0">
              <Badge tone={status.tone} withDot>
                {status.label}
              </Badge>
            </span>
            {/* Вид кнопки макета; ведёт сама строка - ссылка на карточку. */}
            <span aria-hidden className="flex w-[110px] shrink-0 justify-end">
              <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-ink-200 bg-white px-2.5 text-xs font-[550] text-ink-800">
                Открыть <ArrowRight className="size-[11px]" />
              </span>
            </span>
          </div>
        );
      })}
    </Card>
  );
};
